import DailyIframe from '@daily-co/daily-js';
import axios from 'axios';
import { saveSessionContext } from './sessionContextService';
import { resolveProxyUrl } from './proxyUrl';

class DailyPipecatService {
  constructor() {
    this.proxyUrl = resolveProxyUrl({
      envProxyUrl: process.env.REACT_APP_PROXY_URL,
      nodeEnv: process.env.NODE_ENV,
      windowOrigin:
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : null,
    });
    this.agentName = 'aidoctor';
    this.dailyCallObject = null;
    this.isConnected = false;
    this.isCallActive = false;
    this.onRemoteStream = null;
    this.onRemoteAudio = null;
    this.onConversationUpdate = null;
    this.onParticipantUpdated = null;
    this.onSessionEnded = null;
    this._endingCall = false;
    this.isAudioMuted = false;
    this.isVideoMuted = false;
    this.patientName = null;
    this.emiratesId = null;
    this.llmProvider = 'gemini';
    this.consultant = 'sarah';
    this.participants = {};
    this.localParticipantId = null;
    this.localMediaStream = null;
    this._botDisconnectCheckTimer = null;
    this._lastRemoteSeenAt = 0;
    this._remoteVideoTrackId = null;
    this._remoteVideoTrack = null;
    this._remoteAudioTrack = null;
  }

  _buildRemoteStream() {
    const tracks = [];
    if (this._remoteVideoTrack) {
      tracks.push(this._remoteVideoTrack);
    }
    if (this._remoteAudioTrack) {
      tracks.push(this._remoteAudioTrack);
    }
    return tracks.length > 0 ? new MediaStream(tracks) : null;
  }

  _emitRemoteStream() {
    const stream = this._buildRemoteStream();
    if (!stream || !this.onRemoteStream) {
      return;
    }
    console.log('🎬 HeyGen A/V stream updated:', {
      video: Boolean(this._remoteVideoTrack),
      audio: Boolean(this._remoteAudioTrack),
    });
    this.onRemoteStream(stream);
  }

  _notifyRemoteVideoTrack(track) {
    if (!track || track.kind !== 'video') {
      return;
    }
    if (this._remoteVideoTrackId === track.id) {
      return;
    }
    this._remoteVideoTrackId = track.id;
    this._remoteVideoTrack = track;
    console.log('🎥 HeyGen video track detected!');
    this._emitRemoteStream();
  }

  _notifyRemoteAudioTrack(track) {
    if (!track || track.kind !== 'audio') {
      return;
    }
    console.log('🔊 HeyGen audio track detected');
    this._remoteAudioTrack = track;
    this._emitRemoteStream();
  }

  // Set patient information
  setPatientInfo(patientName, emiratesId) {
    this.patientName = patientName;
    this.emiratesId = emiratesId;
  }

  setVoiceProvider(provider) {
    const normalized = String(provider || '').toLowerCase();
    if (normalized === 'sarah') {
      this.consultant = 'sarah';
      this.llmProvider = 'gemini';
      return;
    }
    if (normalized === 'david' || normalized === 'gemini') {
      this.consultant = 'david';
      this.llmProvider = 'gemini';
    }
  }

  getVoiceProvider() {
    return this.consultant;
  }

  getConsultant() {
    return this.consultant;
  }

  async startCall() {
    try {
      if (this.isCallActive) {
        return;
      }

      // Request camera/mic permissions first
      // Use flexible constraints that work better on Android
      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 30, max: 30 },
            facingMode: "user" // Prefer front camera
          },
          audio: {
            sampleRate: { ideal: 16000 },
            channelCount: { ideal: 1 },
            echoCancellation: true,
            noiseSuppression: true
          }
        });
      } catch (videoError) {
        console.warn('⚠️ Video access failed, trying audio-only:', videoError);
        // Fallback to audio-only if video fails (common on Android)
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: {
              sampleRate: { ideal: 16000 },
              channelCount: { ideal: 1 },
              echoCancellation: true,
              noiseSuppression: true
            }
          });
          console.log('✅ Connected with audio-only mode');
        } catch (audioError) {
          console.error('❌ Failed to get media access:', audioError);
          throw new Error('Camera and microphone access denied. Please allow permissions and try again.');
        }
      }
      this.localMediaStream = mediaStream;
      
      // Stop the tracks - Daily will re-request them
      // mediaStream.getTracks().forEach(track => track.stop());
      
      // Use the proxy URL from constructor (http://localhost:8000 for local dev)
      console.log('🔗 Calling proxy at:', this.proxyUrl);
      
      const startResponse = await axios.post(
        `${this.proxyUrl}/api/start-session`,
        {
          patientName: this.patientName,
          emiratesId: this.emiratesId,
          consultant: this.consultant,
          llmProvider: 'gemini',
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      // Check for error response
      if (startResponse.data.error || startResponse.data.status === 'error') {
        const errorMsg = startResponse.data.error || 'Unknown error from server';
        console.error('❌ API returned error:', errorMsg);
        throw new Error(`Failed to start session: ${errorMsg}`);
      }

      let { room_url, token } = startResponse.data;

      // Validate room_url exists and is a string
      if (!room_url || typeof room_url !== 'string') {
        console.error('❌ Invalid room_url in response:', startResponse.data);
        throw new Error(`Invalid response from server: room_url is missing or invalid. Response: ${JSON.stringify(startResponse.data)}`);
      }

      // Clean and validate token (remove any trailing garbage)
      if (token && typeof token === 'string') {
        console.log('🔍 Raw token received:', {
          length: token.length,
          first50: token.substring(0, 50),
          last50: token.substring(Math.max(0, token.length - 50))
        });
        
        // Remove common trailing garbage (Session, Session ID, etc.)
        const originalToken = token;
        token = token.replace(/Session.*$/i, '').trim();
        
        // Also remove anything after a newline or space following the JWT
        // JWT format: header.payload.signature (all base64url: A-Za-z0-9_-)
        const jwtMatch = token.match(/^(eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/);
        if (jwtMatch) {
          token = jwtMatch[1];
          if (token !== originalToken) {
            console.warn('⚠️ Token had trailing garbage, cleaned:', {
              originalLength: originalToken.length,
              cleanedLength: token.length,
              removed: originalToken.substring(token.length)
            });
          } else {
            console.log('✅ Token is clean and valid');
          }
        } else {
          console.error('❌ Could not extract valid JWT from token');
          console.error('Token value:', originalToken);
        }
      } else {
        console.warn('⚠️ Token is missing or not a string:', token);
      }

      console.log('📞 Connecting to session...', { room_url, hasToken: !!token });

      await saveSessionContext({
        roomUrl: room_url,
        patientName: this.patientName,
        emiratesId: this.emiratesId,
        llmProvider: this.consultant,
      });

      const videoTrack = this.localMediaStream?.getVideoTracks?.()[0];
      const audioTrack = this.localMediaStream?.getAudioTracks?.()[0];
      console.log('🎥 Publishing local tracks to Daily:', {
        hasVideo: !!videoTrack,
        hasAudio: !!audioTrack,
      });

      // Create Daily call object (prefer explicit tracks when available)
      this.dailyCallObject = DailyIframe.createCallObject({
        audioSource: audioTrack || true,
        videoSource: videoTrack || true,
      });

      // Set up event handlers BEFORE joining
      this.setupEventHandlers();

      // Join the Daily room
      await this.dailyCallObject.join({
        url: room_url,
        token: token,
        subscribeToTracksAutomatically: true,
      });

      this.isCallActive = true;
      this.isConnected = true;
      this._endingCall = false;
      
      // Store local participant ID
      const localParticipant = this.dailyCallObject.participants().local;
      this.localParticipantId = localParticipant.session_id;
      
      // CRITICAL: Ensure audio/video output is enabled
      this.dailyCallObject.setLocalAudio(true);
      this.dailyCallObject.setLocalVideo(true);
      // updateInputSettings expects processor configs; avoid passing booleans.
      // if (this.dailyCallObject.updateInputSettings) {
      //   await this.dailyCallObject.updateInputSettings({ audio: true, video: true });
      // }

      // If we already have a media stream, attach tracks explicitly to ensure publishing.
      if (this.localMediaStream) {
        const videoTrack = this.localMediaStream.getVideoTracks()[0];
        const audioTrack = this.localMediaStream.getAudioTracks()[0];
        if (videoTrack && this.dailyCallObject.setLocalVideoTrack) {
          await this.dailyCallObject.setLocalVideoTrack(videoTrack);
        }
        if (audioTrack && this.dailyCallObject.setLocalAudioTrack) {
          await this.dailyCallObject.setLocalAudioTrack(audioTrack);
        }
      }
      
      const updatedLocalParticipant = this.dailyCallObject.participants().local;
      console.log('🎥 Local participant tracks:', {
        hasVideo: !!updatedLocalParticipant?.tracks?.video?.track,
        hasAudio: !!updatedLocalParticipant?.tracks?.audio?.track,
      });
      
      // Check if there are already participants in the room
      const allParticipants = this.dailyCallObject.participants();
      console.log('✅ Connected to bot successfully. Participants:', Object.keys(allParticipants).length, allParticipants);
      
      // If only local participant (us), wait for bot to join
      if (Object.keys(allParticipants).length === 1) {
        console.log('⏳ Waiting for bot to join the room... (this can take 15-30 seconds)');
        console.log('💡 The bot is initializing: loading Python, HeyGen service, and connecting to Daily.co');
      }

    } catch (error) {
      console.error('❌ Failed to start call:', error);
      
      // Provide more helpful error messages
      if (error.response) {
        // Server responded with error status (FastAPI uses `detail` for HTTPException)
        const errorData = error.response.data || {};
        const errorMsg =
          errorData.detail ??
          errorData.error ??
          errorData.message ??
          (typeof errorData === 'string' ? errorData : null) ??
          `Server error: ${error.response.status}`;
        console.error('❌ start-session error body:', errorData);
        const resolved =
          typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg);
        throw new Error(`Failed to start session: ${resolved}`);
      } else if (error.request) {
        // Request was made but no response received
        throw new Error(`Failed to connect to server at ${this.proxyUrl}. Is the server running?`);
      } else {
        // Something else happened
        throw new Error(`Failed to start session: ${error.message || 'Unknown error'}`);
      }
    }
  }

  _isSessionEndedMessage(message) {
    if (!message || typeof message !== 'object') return false;
    if (message.type === 'session-ended') return true;
    if (message.data?.type === 'session-ended') return true;
    if (message.label === 'rtvi-ai' && message.type === 'session-ended') return true;
    return false;
  }

  _notifySessionEnded(message) {
    if (this._endingCall) return;
    this._endingCall = true;
    console.log('✅ Consultation complete — returning to Start Conversation', message);
    if (this.onSessionEnded) {
      this.onSessionEnded(message);
    }
  }

  _maybeEndCallAfterBotLeft() {
    // Disabled: transient participant updates can momentarily drop remote list.
    // We only end sessions on explicit session-ended messages from backend.
  }

  _scheduleBotDisconnectCheck() {
    if (this._botDisconnectCheckTimer) {
      clearTimeout(this._botDisconnectCheckTimer);
      this._botDisconnectCheckTimer = null;
    }
    this._botDisconnectCheckTimer = setTimeout(() => {
      this._maybeEndCallAfterBotLeft();
      this._botDisconnectCheckTimer = null;
    }, 2500);
  }

  setupEventHandlers() {
    if (!this.dailyCallObject) return;

    // Handle participant joined
    this.dailyCallObject.on('participant-joined', (event) => {
      console.log('🤖 Bot joined the call:', {
        username: event.participant.user_name,
        userId: event.participant.user_id,
        sessionId: event.participant.session_id
      });
      this.participants[event.participant.session_id] = event.participant;
      if (!event.participant.local) {
        this._lastRemoteSeenAt = Date.now();
      }
      this.notifyParticipantUpdate();
    });

    // Handle participant updated (tracks added/removed)
    this.dailyCallObject.on('participant-updated', (event) => {
      // Silent - only track updates
      this.participants[event.participant.session_id] = event.participant;
      if (!event.participant.local) {
        this._lastRemoteSeenAt = Date.now();
      }
      
      // Check if this is a remote participant with video (any remote participant is the bot)
      const isHeyGen = !event.participant.local;
      
      if (isHeyGen && event.participant.tracks?.video?.track) {
        this._lastRemoteSeenAt = Date.now();
        this._notifyRemoteVideoTrack(event.participant.tracks.video.track);
      }
      if (isHeyGen && event.participant.tracks?.audio?.track) {
        this._lastRemoteSeenAt = Date.now();
        this._notifyRemoteAudioTrack(event.participant.tracks.audio.track);
      }
      
      this.notifyParticipantUpdate();
    });

    // Handle participant left
    this.dailyCallObject.on('participant-left', (event) => {
      console.log('👋 Participant left:', event.participant.user_name || event.participant.user_id);
      delete this.participants[event.participant.session_id];
      this.notifyParticipantUpdate();
      if (!event.participant.local) {
        console.log('ℹ️ Remote participant left event observed; waiting for explicit session-ended signal.');
      }
    });

            // Handle track started
            this.dailyCallObject.on('track-started', (event) => {
              if (event.track.kind === 'audio' && event.participant && !event.participant.local) {
                this._lastRemoteSeenAt = Date.now();
                this._notifyRemoteAudioTrack(event.track);
              }

      if (event.track.kind === 'video' && event.participant && !event.participant.local) {
        console.log('🎥 HeyGen video track started!');
        this._lastRemoteSeenAt = Date.now();
        this._notifyRemoteVideoTrack(event.track);
      }
    });

    // Handle app messages (for conversation updates and transcriptions)
    this.dailyCallObject.on('app-message', (event) => {
      try {
        const message = event.data;

        if (this._isSessionEndedMessage(message)) {
          console.log('👋 session-ended message received:', message);
          this._notifySessionEnded(message);
          return;
        }
        
        // Check if this is an RTVI message and unwrap it
        if (message && message.label === 'rtvi-ai') {
          // Extract the actual message from RTVI wrapper
          const actualMessage = {
            type: message.type,
            data: message.data
          };
          
          // Log for debugging (you can remove this later)
          if (actualMessage.type === 'user-transcription' || 
              actualMessage.type === 'bot-llm-text' || 
              actualMessage.type === 'bot-tts-text') {
            console.log('📝 Transcription:', actualMessage.type, actualMessage.data?.text);
          }
          
          // Forward unwrapped message to Dashboard
          if (this.onConversationUpdate) {
            this.onConversationUpdate(actualMessage);
          }
        } else {
          // Non-RTVI message - forward as is
          if (this.onConversationUpdate) {
            this.onConversationUpdate(message);
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to parse app message:', error);
      }
    });

    // Handle errors
    this.dailyCallObject.on('error', (event) => {
      console.error('❌ Daily error:', event.errorMsg);
    });

    // Handle joining/joined events
    this.dailyCallObject.on('joining-meeting', () => {
      // Silent
    });

    this.dailyCallObject.on('joined-meeting', () => {
      this.isConnected = true;
    });

    // Handle left meeting
    this.dailyCallObject.on('left-meeting', () => {
      console.log('👋 Daily left-meeting event fired');
      this.isConnected = false;
      this.isCallActive = false;
      this._endingCall = false;
    });
  }

  notifyParticipantUpdate() {
    if (this.onParticipantUpdated) {
      this.onParticipantUpdated(this.participants);
    }
  }

  // Get all participants
  getAllParticipants() {
    return this.dailyCallObject ? this.dailyCallObject.participants() : {};
  }

  // Get HeyGen participant stream
  getHeyGenStream() {
    const participants = this.getAllParticipants();
    
    for (const [, participant] of Object.entries(participants)) {
      if (participant.local) continue;
      
      // Any remote participant with video is the bot
      const isHeyGen = !participant.local;
      
      if (isHeyGen && participant.tracks?.video?.track) {
        const videoTrack = participant.tracks.video.track;
        const audioTrack = participant.tracks.audio?.track;
        
        const tracks = [videoTrack];
        if (audioTrack) tracks.push(audioTrack);
        
        return new MediaStream(tracks);
      }
    }
    
    return null;
  }

  // Get local stream
  getLocalStream() {
    if (!this.dailyCallObject) return null;
    
    const localParticipant = this.dailyCallObject.participants().local;
    if (!localParticipant) return null;
    
    const tracks = [];
    if (localParticipant.tracks?.video?.track) {
      tracks.push(localParticipant.tracks.video.track);
    }
    if (localParticipant.tracks?.audio?.track) {
      tracks.push(localParticipant.tracks.audio.track);
    }
    
    return tracks.length > 0 ? new MediaStream(tracks) : null;
  }

  // Toggle audio
  toggleAudio() {
    if (!this.dailyCallObject) return false;
    
    this.isAudioMuted = !this.isAudioMuted;
    this.dailyCallObject.setLocalAudio(!this.isAudioMuted);
    console.log(`🔇 Audio ${this.isAudioMuted ? 'muted' : 'unmuted'}`);
    return this.isAudioMuted;
  }

  // Toggle video
  toggleVideo() {
    if (!this.dailyCallObject) return false;
    
    this.isVideoMuted = !this.isVideoMuted;
    this.dailyCallObject.setLocalVideo(!this.isVideoMuted);
    console.log(`📹 Video ${this.isVideoMuted ? 'muted' : 'unmuted'}`);
    return this.isVideoMuted;
  }

  // Set audio mute state
  setAudioMuted(muted) {
    if (!this.dailyCallObject) return;
    
    this.isAudioMuted = muted;
    this.dailyCallObject.setLocalAudio(!muted);
    console.log(`🔇 Audio ${muted ? 'muted' : 'unmuted'}`);
  }

  // Set video mute state
  setVideoMuted(muted) {
    if (!this.dailyCallObject) return;
    
    this.isVideoMuted = muted;
    this.dailyCallObject.setLocalVideo(!muted);
    console.log(`📹 Video ${muted ? 'muted' : 'unmuted'}`);
  }

  // Get mute states
  getMuteStates() {
    return {
      audio: this.isAudioMuted,
      video: this.isVideoMuted
    };
  }

  async endCall() {
    try {
      this._endingCall = true;
      if (this.dailyCallObject) {
        await this.dailyCallObject.leave();
        await this.dailyCallObject.destroy();
        this.dailyCallObject = null;
      }
      
      this.participants = {};
      this.localParticipantId = null;
      this.isCallActive = false;
      this.isConnected = false;
      this.isAudioMuted = false;
      this.isVideoMuted = false;
      this._lastRemoteSeenAt = 0;
      this._remoteVideoTrackId = null;
      this._remoteVideoTrack = null;
      this._remoteAudioTrack = null;
      if (this._botDisconnectCheckTimer) {
        clearTimeout(this._botDisconnectCheckTimer);
        this._botDisconnectCheckTimer = null;
      }
    } catch (error) {
      console.error('❌ Failed to end call:', error);
    } finally {
      this._endingCall = false;
    }
  }

  // Set callback for remote stream (HeyGen)
  setOnRemoteStream(callback) {
    this.onRemoteStream = callback;
  }

  // Set callback for remote bot audio (single playback path)
  setOnRemoteAudio(callback) {
    this.onRemoteAudio = callback;
  }

  // Set callback for conversation updates
  setOnConversationUpdate(callback) {
    this.onConversationUpdate = callback;
  }

  // Set callback for participant updates
  setOnParticipantUpdated(callback) {
    this.onParticipantUpdated = callback;
  }

  // Set callback when the bot ends the consultation
  setOnSessionEnded(callback) {
    this.onSessionEnded = callback;
  }

  // Check connection states
  isConnectedToBot() {
    return this.isConnected;
  }

  isCallInProgress() {
    return this.isCallActive;
  }

  getConnectionState() {
    return this.dailyCallObject ? this.dailyCallObject.meetingState() : 'not-joined';
  }

  // Send file upload notification to the bot
  sendFileUploadNotification(fileInfo) {
    return this.sendClientMessage({
      t: 'file-upload',
      fileName: fileInfo.fileName || fileInfo.name,
      filePath: fileInfo.filePath || fileInfo.url,
      fileType: fileInfo.fileType || fileInfo.type,
      fileSize: fileInfo.fileSize || fileInfo.size || 0,
    }, 'file upload notification');
  }

  sendPainLocationSelection(locationPayload) {
    return this.sendClientMessage({
      t: 'pain-location-confirmed',
      payload: locationPayload,
    }, 'pain location payload');
  }

  sendPainLocationTimeout(stage = 'gentle') {
    return this.sendClientMessage({
      t: 'pain-location-timeout',
      stage,
    }, `pain location timeout (${stage})`);
  }

  sendPainLocationGuidanceHint() {
    return this.sendClientMessage({
      t: 'pain-location-guidance',
    }, 'pain location guidance');
  }

  sendPainLocationRequestHint() {
    return this.sendClientMessage({
      t: 'pain-location-request-hint',
    }, 'pain location request hint');
  }

  sendPainLocationRetryHint() {
    return this.sendClientMessage({
      t: 'pain-location-retry-hint',
    }, 'pain location retry hint');
  }

  sendUserTurnComplete(text) {
    return this.sendClientMessage({
      t: 'user-turn-complete',
      text: text || '',
    }, 'user turn complete');
  }

  sendTurnTranscript(text) {
    return this.sendClientMessage({
      t: 'turn-transcript',
      text: text || '',
    }, 'turn transcript');
  }

  sendClientMessage(data, label = 'client message') {
    if (!this.dailyCallObject) {
      console.error(`❌ Cannot send ${label}: No call object`);
      return false;
    }
    if (!this.isConnected) {
      console.warn(`⚠️ Sending ${label} while isConnected=false (attempting anyway)`);
    }
    try {
      const message = data;
      console.log(`📤 Sending ${label}:`, message);
      this.dailyCallObject.sendAppMessage(message, '*');
      return true;
    } catch (error) {
      console.error(`❌ Error sending ${label}:`, error);
      return false;
    }
  }
}

export { DailyPipecatService };

